import React, { useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { isHeic, heicTo } from "heic-to";

const thumbsContainer = {
    display: 'grid',
    marginTop: 16,
    gridGap: '0.5rem',
    gridTemplateColumns: '1fr 1fr 1fr',
    marginBottom: '0.5rem',
};

const thumb = {
    display: 'block',
    borderRadius: 2,
    border: '1px solid #eaeaea',
    padding: 2,
    width: '100%',
    height: '100px',
    boxSizing: 'border-box',
    position: 'relative',
    backgroundColor: '#f8f8f8',
};

const img = {
    width: '100%',
    objectFit: 'cover',
    height: '100%',
};

const video = {
    width: '100%',
    objectFit: 'cover',
    height: '100%',
};

const videoPlaceholder = {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f0f0',
    color: '#666',
    fontSize: '12px',
    textAlign: 'center',
};

const playIcon = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '24px',
    color: 'white',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'none',
};

const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: 'gray',
    outline: 'none',
    transition: 'border .25s ease-in-out',
    textAlign: 'center',
};

const focusedStyle = {
    borderColor: '#2196f3',
};

const acceptStyle = {
    borderColor: '#00e676',
};

const rejectStyle = {
    borderColor: '#ff1744',
};

function MediaDropComponent({ onChange }) {
    const [files, setFiles] = useState([]);
    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } =
        useDropzone({
            accept: {
                'image/*': [],
                'video/*': [],
                'video/mp4': [],
                'video/mpeg': [],
                'video/quicktime': [],
                'video/x-msvideo': [],
                'video/webm': [],
                'video/ogg': [],
            },
            onDrop: async (acceptedFiles) => {
                const newFiles = [];

                for (const file of acceptedFiles) {
                    const isVideo = file.type.startsWith('video/');
                    const isImage = file.type.startsWith('image/');
                    const isHeicFile = await isHeic(file) || file.name.toLowerCase().match(/\.(heic|heif)$/);

                    let preview = URL.createObjectURL(file);
                    let displayPreview = preview; // This will be used for actual display

                    // Only convert HEIC files for display purposes
                    if (isHeicFile) {
                        try {
                            const jpegBlob = await heicTo({
                                blob: file,
                                type: "image/jpeg",
                                quality: 0.8
                            });
                            displayPreview = URL.createObjectURL(jpegBlob);
                        } catch (error) {
                            console.error('Error converting HEIC for display:', error);
                        }
                    }

                    const processedFile = Object.assign(file, {
                        preview, // Original file URL (used for videos and form submission)
                        displayPreview, // Converted URL for HEIC, same as preview for others
                        isVideo,
                        isImage: isImage || isHeicFile,
                        isHeicFile,
                    });

                    newFiles.push(processedFile);
                }

                setFiles(prev => [...prev, ...newFiles]);
            },
        });

    const { t } = useTranslation('app');

    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject]
    );

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const removeFile = (fileToRemove) => {
        setFiles((prev) => {
            const updated = prev.filter((f) => f !== fileToRemove);

            // Clean up URLs
            if (fileToRemove.preview) {
                URL.revokeObjectURL(fileToRemove.preview);
            }
            if (fileToRemove.displayPreview && fileToRemove.displayPreview !== fileToRemove.preview) {
                URL.revokeObjectURL(fileToRemove.displayPreview);
            }

            return updated;
        });
    };

    const thumbs = files.map((file, i) => (
        <div style={thumb} key={i}>
            {file.isImage ? (
                <img
                    src={file.displayPreview}
                    style={img}
                    alt={'thumbnail'}
                />
            ) : file.isVideo ? (
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                    <video
                        src={file.preview}
                        style={video}
                        muted
                        preload="metadata"
                        onLoadedMetadata={(e) => {
                            e.target.currentTime = 0.1;
                        }}
                    />
                    <div style={playIcon}>
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="white"
                            style={{ marginLeft: '1px' }}
                        >
                            <path d="M8 5v14l11-7z" />
                        </svg>
                    </div>
                    <div style={{
                        position: 'absolute',
                        bottom: '2px',
                        left: '2px',
                        right: '2px',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        fontSize: '10px',
                        padding: '2px 4px',
                        borderRadius: '2px',
                        textAlign: 'center',
                    }}>
                        {formatFileSize(file.size)}
                    </div>
                </div>
            ) : (
                <div style={videoPlaceholder}>
                    <div style={{ fontSize: '24px', marginBottom: '4px' }}>📄</div>
                    <div style={{ fontWeight: 'bold' }}>Unknown</div>
                </div>
            )}
            <div
                onClick={() => removeFile(file)}
                style={{
                    position: 'absolute',
                    zIndex: 1,
                    top: '-4px',
                    right: '-4px',
                    background: 'white',
                    borderRadius: '100%',
                    cursor: 'pointer',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }}
            >
                <sl-icon name="x-circle-fill" style={{ display: 'block' }} />
            </div>
        </div>
    ));

    useEffect(() => {
        // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
        return () => {
            files.forEach((file) => {
                if (file.preview) {
                    URL.revokeObjectURL(file.preview);
                }
                if (file.displayPreview && file.displayPreview !== file.preview) {
                    URL.revokeObjectURL(file.displayPreview);
                }
            });
        };
    }, [files]);

    useEffect(() => {
        onChange(files);
    }, [files, onChange]);

    return (
        <section className="container">
            <div
                style={{
                    fontSize: 'var(--sl-input-label-font-size-medium)',
                    color: 'var(--sl-input-label-color)',
                    marginBottom: '0.5rem',
                }}
            >
                {t('form.images')}
            </div>
            <div {...getRootProps({ style, className: 'dropzone' })}>
                <input {...getInputProps()} />
                <p>{t('form.dragDrop')}</p>
            </div>
            {!!thumbs.length && <aside style={thumbsContainer}>{thumbs}</aside>}
        </section>
    );
}

export default MediaDropComponent;